"use client";

import { useMemo } from "react";
import type { ReactNode } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { formatCurrency, useBudgetData } from "@/components/app-shell/useBudgetData";
import { colorsForKeys } from "@/lib/colors";

const BLUE = "#0ea5e9";
const GREEN = "#10b981";
const PIE_COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316"];

function ChartCard({
  title,
  description,
  className,
  children,
}: {
  title: string;
  description?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`rounded-2xl border border-border bg-card p-5 shadow-sm ${className ?? ""}`}>
      <h3 className="mb-4 text-base font-medium text-card-foreground">{title}</h3>
      {description ? <p className="-mt-2 mb-4 text-sm text-muted-foreground">{description}</p> : null}
      {children}
    </div>
  );
}

export function ChartSection() {
  const {
    expenses,
    savings,
    investments,
    investmentsByInstrument,
    expenseByCategory,
    goalProgress,
    monthlyComparison,
  } = useBudgetData();

  const tooltipStyle = {
    backgroundColor: "#ffffff",
    border: "1px solid #d1d5db",
    borderRadius: "12px",
    color: "#111111",
    fontSize: "0.8rem",
  };

  const tooltipLabelStyle = { color: "#111111" };
  const tooltipItemStyle = { color: "#111111" };

  const axisStyle = { fontSize: "0.72rem", fill: "var(--muted-foreground)" };

  const spendingByName = useMemo(() => {
    const grouped = expenses.reduce<Record<string, number>>((acc, record) => {
      const key = record.name?.trim() || record.category;
      acc[key] = (acc[key] ?? 0) + Number(record.amount);
      return acc;
    }, {});

    return Object.entries(grouped).map(([name, value]) => ({ name, value }));
  }, [expenses]);

  const savingsBreakdown = useMemo(
    () => savings.map((record) => ({ label: record.note || "Saving", amount: Number(record.amount) })),
    [savings]
  );

  const investmentsBreakdown = useMemo(
    () => investments.map((record) => ({ label: record.instrument, amount: Number(record.amount) })),
    [investments]
  );

  const spendingByNameColors = useMemo(() => colorsForKeys(spendingByName.map((item) => item.name)), [spendingByName]);
  const savingsBreakdownColors = useMemo(
    () => colorsForKeys(savingsBreakdown.map((item) => item.label)),
    [savingsBreakdown]
  );
  const investmentsBreakdownColors = useMemo(
    () => colorsForKeys(investmentsBreakdown.map((item) => item.label)),
    [investmentsBreakdown]
  );

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <ChartCard
        title="Spending by Name"
        description="Distribution across individual expense names"
        className="xl:col-span-2"
      >
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={spendingByName} dataKey="value" nameKey="name" outerRadius={110}>
              {spendingByName.map((item, index) => (
                <Cell key={`${item.name}-${index}`} fill={spendingByNameColors[index]} />
              ))}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} formatter={(value: number) => formatCurrency(value)} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Savings Breakdown" description="Allocation of savings">
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={savingsBreakdown} dataKey="amount" nameKey="label" outerRadius={100} innerRadius={50}>
              {savingsBreakdown.map((item, index) => (
                <Cell key={`${item.label}-${index}`} fill={savingsBreakdownColors[index]} />
              ))}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} formatter={(value: number) => formatCurrency(value)} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Investments Breakdown" description="Allocation of investments">
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie data={investmentsBreakdown} dataKey="amount" nameKey="label" outerRadius={100} innerRadius={50}>
              {investmentsBreakdown.map((item, index) => (
                <Cell key={`${item.label}-${index}`} fill={investmentsBreakdownColors[index]} />
              ))}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} formatter={(value: number) => formatCurrency(value)} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Investment Allocation">
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={investmentsByInstrument}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={axisStyle} />
            <YAxis tick={axisStyle} tickFormatter={(value: number) => formatCurrency(value)} width={80} />
            <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} formatter={(value: number) => formatCurrency(value)} />
            <Bar dataKey="amount" fill={GREEN} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Expense Breakdown">
        <ResponsiveContainer width="100%" height={240}>
          <PieChart>
            <Pie
              data={expenseByCategory}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={84}
            >
              {expenseByCategory.map((_, index) => (
                <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} formatter={(value: number) => formatCurrency(value)} />
          </PieChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Goal Progress">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={goalProgress} layout="vertical">
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" domain={[0, 100]} tick={axisStyle} tickFormatter={(value: number) => `${value}%`} />
            <YAxis dataKey="name" type="category" tick={axisStyle} width={90} />
            <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} formatter={(value: number) => `${value}%`} />
            <Bar dataKey="progress" fill={BLUE} radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Monthly Savings vs Expenses">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={monthlyComparison}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
            <XAxis dataKey="month" tick={axisStyle} />
            <YAxis tick={axisStyle} tickFormatter={(value: number) => formatCurrency(value)} width={80} />
            <Tooltip contentStyle={tooltipStyle} labelStyle={tooltipLabelStyle} itemStyle={tooltipItemStyle} formatter={(value: number) => formatCurrency(value)} />
            <Bar dataKey="savings" fill={GREEN} radius={[6, 6, 0, 0]} />
            <Bar dataKey="expenses" fill="#ef4444" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}
